'use client';
import { useCallback, useRef } from 'react';
import addStyle from 'dom-lib/addStyle';
import toggleClass from '../utils/toggleClass';
import useUpdateEffect from './useUpdateEffect';
import isSupportTouchEvent from '../utils/isSupportTouchEvent';
import defer from '../utils/defer';
import { SCROLLBAR_WIDTH } from '../constants';
/**
 * Update the position of the table according to the scrolling information of the table.
 * @param props
 * @returns
 */
var usePosition = function usePosition(props) {
  var data = props.data,
    height = props.height,
    tableWidth = props.tableWidth,
    tableRef = props.tableRef,
    prefix = props.prefix,
    setCssPosition = props.setCssPosition,
    wheelWrapperRef = props.wheelWrapperRef,
    headerWrapperRef = props.headerWrapperRef,
    affixHeaderWrapperRef = props.affixHeaderWrapperRef,
    tableHeaderRef = props.tableHeaderRef,
    scrollX = props.scrollX,
    scrollY = props.scrollY,
    contentWidth = props.contentWidth,
    shouldFixedColumn = props.shouldFixedColumn;
  var duration = useRef(0);
  var bezier = useRef('linear');
  var getScrollCellGroups = useCallback(function () {
    var _tableRef$current;
    return ((_tableRef$current = tableRef.current) === null || _tableRef$current === void 0 ? void 0 : _tableRef$current.querySelectorAll("." + prefix('cell-group-scroll'))) || [];
  }, [prefix, tableRef]);
  var getFixedLeftCellGroups = useCallback(function () {
    var _tableRef$current2;
    return (_tableRef$current2 = tableRef.current) === null || _tableRef$current2 === void 0 ? void 0 : _tableRef$current2.querySelectorAll("." + prefix('cell-group-fixed-left'));
  }, [prefix, tableRef]);
  var getFixedRightCellGroups = useCallback(function () {
    var _tableRef$current3;
    return (_tableRef$current3 = tableRef.current) === null || _tableRef$current3 === void 0 ? void 0 : _tableRef$current3.querySelectorAll("." + prefix('cell-group-fixed-right'));
  }, [prefix, tableRef]);
  var updateWheelElementPosition = useCallback(function (fixedCell) {
    if (wheelWrapperRef !== null && wheelWrapperRef !== void 0 && wheelWrapperRef.current) {
      // The animation when the mobile device touches and scrolls.
      var wheelStyle = isSupportTouchEvent() ? {
        'transition-duration': duration.current + "ms",
        'transition-timing-function': bezier.current
      } : {};
      setCssPosition.current(wheelStyle, fixedCell ? 0 : scrollX.current, scrollY.current);
      addStyle(wheelWrapperRef.current, wheelStyle);
    }
  }, [scrollX, scrollY, setCssPosition, wheelWrapperRef]);
  var updatePositionByFixedCell = useCallback(function () {
    var wheelGroupStyle = {};
    var scrollGroups = getScrollCellGroups();
    var fixedLeftGroups = getFixedLeftCellGroups();
    var fixedRightGroups = getFixedRightCellGroups();
    setCssPosition.current(wheelGroupStyle, scrollX.current, 0);
    var scrollArrayGroups = Array.from(scrollGroups);
    for (var i = 0; i < scrollArrayGroups.length; i++) {
      var group = scrollArrayGroups[i];
      addStyle(group, wheelGroupStyle);
    }
    updateWheelElementPosition(true);

    // Whether to show the horizontal scroll bar
    var hasHorizontalScrollbar = contentWidth.current > tableWidth.current;
    var scrollbarWidth = hasHorizontalScrollbar ? 0 : SCROLLBAR_WIDTH;
    var leftShadowClassName = prefix('cell-group-left-shadow');
    var rightShadowClassName = prefix('cell-group-right-shadow');
    var showLeftShadow = scrollX.current < 0;
    var showRightShadow = tableWidth.current - contentWidth.current - scrollbarWidth !== scrollX.current;
    toggleClass(fixedLeftGroups, leftShadowClassName, showLeftShadow);
    toggleClass(fixedRightGroups, rightShadowClassName, showRightShadow);
  }, [contentWidth, getFixedLeftCellGroups, getFixedRightCellGroups, getScrollCellGroups, updateWheelElementPosition, prefix, scrollX, tableWidth, setCssPosition]);

  /**
   * Update the position of the table according to the scrolling information of the table.
   * @param nextDuration CSS transition-duration
   * @param nextBezier CSS transition-timing-function
   */
  var updatePosition = useCallback(function (nextDuration, nextBezier) {
    if (nextDuration) {
      duration.current = nextDuration;
    }
    if (nextBezier) {
      bezier.current = nextBezier;
    }

    // When there are fixed columns.
    if (shouldFixedColumn) {
      updatePositionByFixedCell();
    } else {
      var _affixHeaderElement$h;
      var headerStyle = {};
      setCssPosition.current(headerStyle, scrollX.current, 0);
      var headerElement = headerWrapperRef === null || headerWrapperRef === void 0 ? void 0 : headerWrapperRef.current;
      var affixHeaderElement = affixHeaderWrapperRef === null || affixHeaderWrapperRef === void 0 ? void 0 : affixHeaderWrapperRef.current;
      updateWheelElementPosition();
      headerElement && addStyle(headerElement, headerStyle);
      if (affixHeaderElement !== null && affixHeaderElement !== void 0 && (_affixHeaderElement$h = affixHeaderElement.hasChildNodes) !== null && _affixHeaderElement$h !== void 0 && _affixHeaderElement$h.call(affixHeaderElement)) {
        addStyle(affixHeaderElement === null || affixHeaderElement === void 0 ? void 0 : affixHeaderElement.firstChild, headerStyle);
      }
    }
    if (tableHeaderRef !== null && tableHeaderRef !== void 0 && tableHeaderRef.current) {
      toggleClass(tableHeaderRef.current, prefix('cell-group-shadow'), scrollY.current < 0);
    }
  }, [affixHeaderWrapperRef, updateWheelElementPosition, headerWrapperRef, prefix, scrollX, scrollY, shouldFixedColumn, tableHeaderRef, setCssPosition, updatePositionByFixedCell]);
  useUpdateEffect(function () {
    if (scrollY.current !== 0) {
      updatePosition();
    }
  }, [height, data]);
  return {
    forceUpdatePosition: updatePosition,
    deferUpdatePosition: function deferUpdatePosition(nextDuration, nextBezier) {
      defer(function () {
        updatePosition(nextDuration, nextBezier);
      });
    }
  };
};
export default usePosition;